import { useState, type FormEvent } from "react";
import { Plus, Check } from "lucide-react";
import { Dialog } from "./components";
import {
  id,
  markExample,
  parseMarked,
  wordSchema,
  examplesFor,
  type WordSense,
  type Snapshot,
  type ExampleSentence,
} from "./domain";
import { suggest, copyStarter } from "./content";
import { db, saveWord } from "./db";
export default function Editor({
  s,
  word,
  onClose,
  onSaved,
}: {
  s: Snapshot;
  word?: WordSense;
  onClose: () => void;
  onSaved: (m: string) => void;
}) {
  const existing = word
    ? examplesFor(s, word.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    : [];
  const [lemma, setLemma] = useState(word?.lemma ?? ""),
    [meaning, setMeaning] = useState(word?.meaning ?? ""),
    [sense, setSense] = useState(word?.sense ?? ""),
    [pos, setPos] = useState(word?.pos ?? "verb"),
    [ipa, setIpa] = useState(word?.ipa ?? ""),
    [cefr, setCefr] = useState(word?.cefr ?? "A1"),
    [kind, setKind] = useState(word?.kind ?? "word");
  const [examples, setExamples] = useState(
    existing.length
      ? existing.map((e) => ({ marked: markExample(e), tr: e.tr }))
      : [{ marked: "", tr: "" }],
  );
  const [collocations, setCollocations] = useState(word?.collocations.join("; ") ?? ""),
    [commonError, setCommonError] = useState(word?.commonError ?? ""),
    [tags, setTags] = useState(word?.tags.join("; ") ?? ""),
    [notes, setNotes] = useState(word?.notes ?? ""),
    [decks, setDecks] = useState(word?.deckIds ?? ["personal"]);
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [suggestionUsed, setSuggestionUsed] = useState(false),
    [approved, setApproved] = useState(false);
  const suggestions = suggest(lemma);
  function applySuggestion(index: number) {
    const r = copyStarter(suggestions[index]);
    setMeaning(r.word.meaning);
    setSense(r.word.sense);
    setPos(r.word.pos);
    setIpa(r.word.ipa);
    setCefr(r.word.cefr);
    setKind(r.word.kind);
    setCollocations(r.word.collocations.join("; "));
    setCommonError(r.word.commonError);
    setExamples([{ marked: markExample(r.example), tr: r.example.tr }]);
    setSuggestionUsed(true);
    setApproved(false);
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (suggestionUsed && !approved)
        throw new Error("Öneriyi kontrol ettiğini onayla; gerekirse alanları düzenle.");
      const now = new Date().toISOString(),
        wordId = word?.id ?? id(),
        split = (v: string) =>
          v
            .split(";")
            .map((x) => x.trim())
            .filter(Boolean);
      const w = wordSchema.parse({
        id: wordId,
        lemma: lemma.trim(),
        meaning: meaning.trim(),
        sense: sense.trim() || meaning.trim(),
        pos,
        ipa,
        cefr,
        kind,
        collocations: split(collocations),
        commonError,
        tags: split(tags),
        notes,
        deckIds: decks.length ? decks : ["personal"],
        favorite: word?.favorite ?? false,
        difficult: word?.difficult ?? false,
        active: true,
        deleted: false,
        source: word?.source ?? "personal",
        createdAt: word?.createdAt ?? now,
      });
      const exs: ExampleSentence[] = examples.map((ex) => {
        const parsed = parseMarked(ex.marked),
          same = existing.find(
            (old) =>
              old.en === parsed.en &&
              old.tr === ex.tr &&
              JSON.stringify(old.spans) === JSON.stringify(parsed.spans),
          );
        return (
          same ?? {
            id: id(),
            wordId,
            ...parsed,
            tr: ex.tr.trim(),
            createdAt: now,
          }
        );
      });
      await saveWord(w, exs);
      onSaved(
        word
          ? "Kelime güncellendi. Tekrar geçmişi korundu."
          : "Kelime kütüphanene eklendi.",
      );
      onClose();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Kelime kaydedilemedi. Alanları kontrol edip yeniden dene.",
      );
      requestAnimationFrame(() => document.getElementById("editor-error")?.focus());
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={word ? "Kelimeyi düzenle" : "Kendi kelimeni ekle"}
      onClose={onClose}
      wide
    >
      <form onSubmit={submit} className="editor stack">
        <p className="muted">Fielse’de gördüğün kelimeyi, öğrendiğin anlamıyla ekle.</p>
        <label>
          Kelime veya ifade
          <input
            autoFocus
            lang="en"
            required
            maxLength={150}
            autoCapitalize="none"
            autoCorrect="off"
            value={lemma}
            onChange={(e) => {
              setLemma(e.target.value);
              setSuggestionUsed(false);
            }}
            placeholder="mistake"
          />
        </label>
        {!word && lemma.trim() && (
          <div className="suggestions">
            {suggestions.length ? (
              <>
                <p>
                  Çevrimdışı örnek defterinde {suggestions.length} anlam bulundu.
                  Alanları doldurup kontrol edebilirsin.
                </p>
                {suggestions.map((r, i) => (
                  <button
                    type="button"
                    className="secondary"
                    key={r.word.id}
                    onClick={() => applySuggestion(i)}
                  >
                    {r.word.meaning}
                  </button>
                ))}
              </>
            ) : (
              <p>
                Bu kelime için yerel öneri yok. Anlamını ve cümlesini Fielse notlarından
                ekleyebilirsin. Otomatik içerik üretilmez.
              </p>
            )}
          </div>
        )}
        <div className="form-grid">
          <label>
            Türkçe anlam
            <input
              required
              value={meaning}
              onChange={(e) => setMeaning(e.target.value)}
              placeholder="hata"
            />
          </label>
          <label>
            Anlam ayrımı <span className="muted">(isteğe bağlı)</span>
            <input
              value={sense}
              onChange={(e) => setSense(e.target.value)}
              placeholder="Örn. restoran siparişi"
            />
          </label>
        </div>
        <div className="form-grid three">
          <label>
            Kelime türü
            <select value={pos} onChange={(e) => setPos(e.target.value)}>
              {[
                "verb",
                "noun",
                "adjective",
                "adverb",
                "preposition",
                "determiner",
                "pronoun",
                "phrase",
                "other",
              ].map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </label>
          <label>
            Düzey
            <select
              value={cefr}
              onChange={(e) => setCefr(e.target.value as WordSense["cefr"])}
            >
              {["A1", "A2", "B1", "B2"].map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </label>
          <label>
            Öğrenme birimi
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as WordSense["kind"])}
            >
              <option value="word">Kelime</option>
              <option value="phrase">Kalıp / ifade</option>
            </select>
          </label>
        </div>
        <label>
          Amerikan İngilizcesi IPA <span className="muted">(isteğe bağlı)</span>
          <input
            value={ipa}
            onChange={(e) => setIpa(e.target.value)}
            placeholder="/mɪˈsteɪk/"
          />
        </label>
        <fieldset className="examples-fieldset">
          <legend>Örnek cümleler</legend>
          <p className="muted">
            Yalnızca hedef birimi [köşeli parantez] içine al: I made a [mistake]
            yesterday.
          </p>
          {examples.map((ex, i) => (
            <div className="example-editor stack" key={i}>
              <label>
                İngilizce cümle {i + 1}
                <textarea
                  lang="en"
                  required
                  value={ex.marked}
                  onChange={(e) =>
                    setExamples((old) =>
                      old.map((r, j) =>
                        j === i ? { ...r, marked: e.target.value } : r,
                      ),
                    )
                  }
                  placeholder="I made a [mistake] yesterday."
                />
              </label>
              <label>
                Türkçe çeviri {i + 1}
                <textarea
                  required
                  value={ex.tr}
                  onChange={(e) =>
                    setExamples((old) =>
                      old.map((r, j) => (j === i ? { ...r, tr: e.target.value } : r)),
                    )
                  }
                  placeholder="Dün bir hata yaptım."
                />
              </label>
              {examples.length > 1 && (
                <button
                  type="button"
                  className="text-button"
                  onClick={() => setExamples((old) => old.filter((_, j) => j !== i))}
                >
                  Bu örneği formdan çıkar
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="secondary"
            onClick={() => setExamples((old) => [...old, { marked: "", tr: "" }])}
          >
            <Plus size={17} />
            Yeni örnek cümle
          </button>
        </fieldset>
        <details>
          <summary>Kalıplar, etiketler ve notlar</summary>
          <div className="stack">
            <label>
              Yaygın kullanımlar <span className="muted">(; ile ayır)</span>
              <input
                value={collocations}
                onChange={(e) => setCollocations(e.target.value)}
                placeholder="make a mistake"
              />
            </label>
            <label>
              Yaygın hata açıklaması
              <input
                value={commonError}
                onChange={(e) => setCommonError(e.target.value)}
                placeholder="do a mistake yerine make a mistake kullanılır."
              />
            </label>
            <label>
              Etiketler <span className="muted">(; ile ayır)</span>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="Fielse; iş"
              />
            </label>
            <fieldset>
              <legend>Koleksiyonlar</legend>
              {s.decks.map((d) => (
                <label className="check-label" key={d.id}>
                  <input
                    type="checkbox"
                    checked={decks.includes(d.id)}
                    onChange={(e) =>
                      setDecks(
                        e.target.checked
                          ? [...decks, d.id]
                          : decks.filter((v) => v !== d.id),
                      )
                    }
                  />
                  {d.name}
                </label>
              ))}
            </fieldset>
            <label>
              Kişisel not
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
          </div>
        </details>
        {suggestionUsed && (
          <label className="check-label">
            <input
              type="checkbox"
              checked={approved}
              onChange={(e) => setApproved(e.target.checked)}
            />
            Önerinin anlamını ve cümlesini kontrol ettim.
          </label>
        )}
        {error && (
          <p className="error" id="editor-error" role="alert" tabIndex={-1}>
            {error}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={onClose}>
            Vazgeç
          </button>
          <button className="primary" disabled={busy}>
            <Check size={18} />
            {busy ? "Kaydediliyor…" : "Kelimeyi kaydet"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
export async function createCollection(name: string) {
  name = name.trim();
  if (!name || name.length > 100)
    throw new Error("Koleksiyon adı 1–100 karakter olmalı.");
  if (
    (await db.decks.toArray()).some(
      (d) => d.name.toLocaleLowerCase("tr-TR") === name.toLocaleLowerCase("tr-TR"),
    )
  )
    throw new Error("Bu koleksiyon zaten var.");
  await db.decks.add({ id: id(), name, description: "" });
}
